<template>
  <MultiSelectPicker
    v-if="isReady"
    v-model="selectedOptions"
    :options="typeOptions"
    id-attribute="type"
    display-attribute="display"
    dropdown-alignment="left"
    @update:model-value="onInput"
  >
    <template #button-display>
      <div class="selected-type-label">
        <div class="contacts-icon">
          <font-awesome-icon icon="user-friends"></font-awesome-icon>
        </div>

        <div class="selected-type-text">{{ displayName }}</div>
      </div>
    </template>
  </MultiSelectPicker>
</template>

<script lang="ts" setup>
import { ContactType } from '@/features/contacts/models/contactType';
import MultiSelectPicker from '@/shared/components/MultiSelectPicker.vue';
import { computed, onMounted, PropType, ref } from 'vue';

interface TypeOption {
  type: string;
  display: string;
}

const props = defineProps({
  modelValue: {
    type: Array as PropType<ContactType[]>,
    required: true,
  },
});

const emit = defineEmits<{
  (e: 'updated-type', value: ContactType[]): void;
  (e: 'update:modelValue', value: ContactType[]): void;
}>();

const typeOptions = toOptionObjects([ContactType.Visitor, ContactType.Lead, ContactType.User]);
const selectedOptions = ref<TypeOption[]>([]);
const isReady = ref(false);

const displayName = computed(() => {
  if (selectedOptions.value.length === 0 || selectedOptions.value.length === 3) {
    return 'Visitors, Leads, Users';
  }
  return selectedOptions.value.map((option: TypeOption) => getFriendlyName(option.type)).join(', ');
});

onMounted(() => {
  if (shouldAllTypesBeSelected(props.modelValue)) {
    selectedOptions.value = toOptionObjects([ContactType.Visitor, ContactType.Lead, ContactType.User]);
  } else {
    selectedOptions.value = toOptionObjects(props.modelValue);
  }
  isReady.value = true;
});

function onInput(newSelectedOptions: TypeOption[]) {
  const selectedTypes = newSelectedOptions.map((option) => option.type);
  emit('updated-type', selectedTypes);
  emit('update:modelValue', selectedTypes);
}

function toOptionObjects(options: string[]): TypeOption[] {
  return options.map((type) => ({ type, display: getFriendlyName(type) }));
}

function getFriendlyName(type: string) {
  return type.charAt(0).toUpperCase() + type.slice(1) + 's';
}

function shouldAllTypesBeSelected(types: string[]) {
  return !types || types.length === 0;
}
</script>
<style scoped lang="scss">
.selected-type-label {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  .selected-type-text {
    padding-left: 0.5em;
  }
}
</style>
